<template lang="pug">
article.the-news-view
  header
    VContainer.pb-6.pt-6
      VRow
        VCol
          VvBreadcrumbs.mb-4(:breadcrumbs="breadcrumbs")
          HH.text-h1 {{ title }}

  HSection.surface
    VContainer
      VRow
        VCol
          div.mt-5(v-html="bodyHtml")
</template>

<script setup lang="ts">
import { computed } from "vue";
import HH from "~/headings/components/HH.vue";
import HSection from "~/headings/components/HSection.vue";
import { getHomeLocation } from "~/routing/locations";

const runtimeConfig = useRuntimeConfig();

interface Props {
  bodyHtml: string;
  title: string;
  created: Date;
  changed: Date;
}

const props = defineProps<Props>();

/**
 * The document title, for the `title` tag.
 */
const documentTitle = computed<string>(() => {
  return `${props.title} | ${runtimeConfig.public.site.name} News`;
});

/**
 * The array of links to display at the very top of the page, indicating the logical "parents" of the current page.
 */
const breadcrumbs = computed(() => [
  {
    // TODO: If we ever have a page other than "/" where news is listed, this should rather point there.
    to: getHomeLocation(),
    title: "News",
    plainTitle: "News",
  },
]);

/**
 * JSON LD for structured data
 * https://medium.com/@fmoessle/json-ld-with-vue-js-72cf1ac2af66
 */
const articleJsonLd = computed(() => ({
  "@context": "https://schema.org",
  "@type": "NewsArticle",
  headline: props.title,
  datePublished: props.created,
  dateModified: props.changed,
}));

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify(articleJsonLd.value),
    },
  ],
  title: documentTitle,
  meta: [
    {
      name: "og:title",
      content: props.title,
    },
  ],
});
</script>
